import type { GeneralSettings } from "@nectarpay/api-contracts/account";
import { AccountSettings } from "@/account/types/accountSettings";
import { AccountStore, type AccountUsage } from "@/account/types/account";
import type {
  GenerateKioskTokenHashResponse,
  KioskSettingsPatchPayload,
  KioskSettingsResponse,
} from "@/kiosk/types/kiosk";
import axios from "@/app/plugins/axios";
import { defineStore } from "pinia";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import config from "@/app/configs";
import { AxiosResponse } from "axios";
import { NectarStatus, AccountPlan, UserRoleStatus } from "@/shared/enums/status.enum";
import { useAuthStore } from "@/auth/stores/auth";
import { updateCurrentAccount } from "@/shared/utils/sessionStorage.utils";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { getErrorMessage } from "@/shared/utils/errorMessage";

type AccountPlanInfo = AccountStore["plan"];
type AccountSubscriptionInfo = AccountStore["subscription"];

interface AccountLinkResponse {
  url?: string;
}

const toastApiError = (error: unknown): void => {
  useSnackBarStore().toastError({ message: getErrorMessage(error) });
};

export const useAccountStore = defineStore("account", {
  state: (): AccountStore => ({
    account: null,
    loading: false,
    linkLoading: false,
    completeOnboardingLinkLoading: false,
    subscriptionCancelLinkLoading: false,
    subscriptionUpdateLinkLoading: false,
    checkoutSessionLinkLoading: false,
    planLoading: false,
    accountUsageLoading: false,
    productLoading: false,
    subscription: {
      id: "",
      cancelAtPeriodEnd: false,
      periodEnd: 0,
    },
    plan: {
      name: "",
      billingCycle: "",
      amount: 0,
    },
    products: [
      {
        id: "",
        name: "",
        description: "",
        price: {
          recurring: {
            interval: "string",
          },
          unit_amount: 0,
        },
      },
    ],
  }),
  getters: {
    isLoading: (state) => state.loading,
    isLinkLoading: (state) => state.linkLoading,
    isCompleteOnboardingLinkLoading: (state) => state.completeOnboardingLinkLoading,
    isPlanLoading: (state) => state.planLoading,
    isAccountUsageLoading: (state) => state.accountUsageLoading,
    isProductLoading: (state) => state.productLoading,
    getAccountDisplayName: (state): string =>
      state.account?.businessName ||
      state.account?.business_profile?.name ||
      state.account?.email ||
      state.account?.id ||
      "--",
    getAccountInitials(): string {
      const getInitials = (name: string) => {
        const parts = name.split(" ") ?? [];
        let initials;

        if (parts.length > 1) {
          const first = (parts.shift() ?? "").charAt(0);
          const last = (parts.pop() ?? "").charAt(0);
          initials = `${first}${last}`.trim();
        } else {
          initials = name.substring(0, 2);
        }

        return initials.toUpperCase();
      };

      return getInitials(this.getAccountDisplayName);
    },
    getAccountSelected(): boolean {
      return !!this.account;
    },
    getDefaultCurrency(): string {
      return this.account?.default_currency || "CAD";
    },
    getAccountId(): string | undefined {
      return this.account?.id;
    },
  },
  actions: {
    // We clear this way to use in Account page, we don't want to clear the current account
    clearStore(): void {
      this.plan = {
        name: "",
        billingCycle: "",
        amount: 0,
      };
      this.products = [
        {
          id: "",
          name: "",
          description: "",
          price: {
            recurring: {
              interval: "string",
            },
            unit_amount: 0,
          },
        },
      ];
    },

    setCurrentAccount(newAccount: AccountStore["account"]): void {
      this.account = newAccount;
    },

    async fetchPlanInfo(): Promise<void> {
      try {
        this.planLoading = true;
        const response = await axios.get("/account/plan");
        const plan = response.data.plan as AccountPlanInfo;
        this.plan = plan;
        const subscription = response.data.subscription as AccountSubscriptionInfo;
        this.subscription = subscription;
      } catch (error) {
        toastApiError(error);
      } finally {
        this.planLoading = false;
      }
    },

    // unused but might need in future(?)
    async getAccountPlan(): Promise<AccountPlanInfo | undefined> {
      try {
        this.planLoading = true;
        const response = await axios.get("/account/plan");
        return response.data.plan as AccountPlanInfo;
      } catch (error) {
        toastApiError(error);
      } finally {
        this.planLoading = false;
      }
    },

    // unused but might need in future(?)
    async getAccountSubscription(): Promise<AccountSubscriptionInfo | undefined> {
      try {
        this.planLoading = true;
        const response = await axios.get("/account/plan");
        return response.data.subscription as AccountSubscriptionInfo;
      } catch (error) {
        toastApiError(error);
      } finally {
        this.planLoading = false;
      }
    },

    async fetchProductOptions(): Promise<void> {
      try {
        this.productLoading = true;
        const response = await axios.get("/account/products");
        const products = response.data as AccountStore["products"];
        this.products = products;
      } catch (error) {
        toastApiError(error);
      } finally {
        this.productLoading = false;
      }
    },

    async getAccountUsage(): Promise<AccountUsage | undefined> {
      try {
        this.accountUsageLoading = true;
        const response = await axios.get("/account/usage");
        return response.data as AccountUsage;
      } catch (error) {
        toastApiError(error);
      } finally {
        this.accountUsageLoading = false;
      }
    },

    async createAccountLink(
      returnURL: string,
      refreshURL?: string,
    ): Promise<AccountLinkResponse | undefined> {
      this.linkLoading = true;
      const link = await axios
        .post("/account/link", {
          type: "account_onboarding",
          refresh_url: refreshURL ? refreshURL : returnURL,
          return_url: returnURL,
          collection_options: {
            fields: "eventually_due",
            future_requirements: "include",
          },
        })
        .then((res: AxiosResponse) => {
          return res.data as AccountLinkResponse;
        })
        .catch((error) => {
          const errorCode = (error as { data?: { code?: string } } | null | undefined)?.data?.code;
          if (errorCode !== "parameter_invalid_empty") {
            toastApiError(error);
          }
          return undefined;
        })
        .finally(() => (this.linkLoading = false));

      return link;
    },

    async createPortalCancelLink(): Promise<AccountLinkResponse | undefined> {
      this.subscriptionCancelLinkLoading = true;
      await this.fetchPlanInfo();
      const link = await axios
        .post("/account/portal/update", {
          flow_data: {
            type: "subscription_cancel",
            subscription_cancel: {
              subscription: this.subscription.id,
            },
            after_completion: {
              type: "redirect",
              redirect: {
                return_url: config?.links?.portalReturn,
              },
            },
          },
        })
        .then((res: AxiosResponse) => {
          return res.data as AccountLinkResponse;
        })
        .catch((error) => {
          toastApiError(error);
          return undefined;
        })
        .finally(() => (this.subscriptionCancelLinkLoading = false));

      return link;
    },

    async createPortalUpdateLink(): Promise<AccountLinkResponse | undefined> {
      this.subscriptionUpdateLinkLoading = true;
      await this.fetchPlanInfo();
      const link = await axios
        .post("/account/portal/update", {
          flow_data: {
            type: "subscription_update",
            subscription_update: {
              subscription: this.subscription.id,
            },
            after_completion: {
              type: "redirect",
              redirect: {
                return_url: config?.links?.portalReturn,
              },
            },
          },
        })
        .then((res: AxiosResponse) => {
          return res.data as AccountLinkResponse;
        })
        .catch((error) => {
          toastApiError(error);
          return undefined;
        })
        .finally(() => (this.subscriptionUpdateLinkLoading = false));

      return link;
    },

    async createCheckoutSessionLink(): Promise<AccountLinkResponse | undefined> {
      this.checkoutSessionLinkLoading = true;
      const link = await axios
        .post("/account/checkoutSession", {
          success_url: config?.links?.portalReturn,
          payment_method_types: ["card"],
          mode: "subscription",
        })
        .then((res: AxiosResponse) => {
          return res.data as AccountLinkResponse;
        })
        .catch((error) => {
          toastApiError(error);
          return undefined;
        })
        .finally(() => (this.checkoutSessionLinkLoading = false));

      return link;
    },

    async completeOnboarding() {
      this.completeOnboardingLinkLoading = true;
      await axios
        .post("/account/completeOnboarding", {})
        .then(() => {
          useAuthStore().accountStatus = NectarStatus.ACTIVE;
          if (this.account) {
            this.setCurrentAccount({
              ...this.account,
              nectarStatus: NectarStatus.ACTIVE,
            });
          }
          const newAccount = {
            role: UserRoleStatus.OWNER,
            plan: AccountPlan.STARTER,
            nectarStatus: NectarStatus.ACTIVE,
          };
          updateCurrentAccount(newAccount);
        })

        .catch((error) => {
          toastApiError(error);
        })
        .finally(() => (this.completeOnboardingLinkLoading = false));
    },

    // unused but might need in future(?)
    async getPlanName(): Promise<string | undefined> {
      try {
        this.planLoading = true;
        const response = await axios.get("/account/plan");

        if (response.data.plan.name) {
          return response.data.plan.name as string;
        } else {
          return AccountPlan.STARTER;
        }
      } catch (error) {
        toastApiError(error);
      } finally {
        this.planLoading = false;
      }
    },

    async createTaxSession(): Promise<ReturnType<typeof loadConnectAndInitialize> | null> {
      const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
      const fetchClientSecret = async () => {
        const response = await axios.post("/account/session");
        const { client_secret } = response.data;
        return client_secret as string;
      };
      try {
        const stripeConnectInstance = loadConnectAndInitialize({
          publishableKey: publishableKey,
          fetchClientSecret: fetchClientSecret,
        });

        return stripeConnectInstance;
      } catch (error) {
        toastApiError(error);
        return null;
      }
    },

    async updateGeneralSettings(payload: GeneralSettings): Promise<GeneralSettings | undefined> {
      const accountId = this.account?.id;
      try {
        const { data } = await axios.patch<GeneralSettings>("/account/settings/general", payload);
        if (this.account?.id === accountId && this.account) {
          this.account.businessName = data.businessName;
        }
        const userAccount = accountId ? useAuthStore().user?.accounts?.[accountId] : undefined;
        if (userAccount) {
          userAccount.name = data.businessName;
        }
        return data;
      } catch (error) {
        toastApiError(error);
        return undefined;
      }
    },

    async updateAccountSettings(payload: AccountSettings) {
      try {
        // This helps keep settings state updated
        const mergedSettings = JSON.parse(
          JSON.stringify(this.account?.accountSettings || {}),
        ) as AccountSettings;

        // Merge the payload into the copied settings
        Object.entries(payload).forEach(([key, value]) => {
          const typedKey = key as keyof AccountSettings;
          if (typeof value === "object" && value !== null) {
            mergedSettings[typedKey] = {
              ...mergedSettings[typedKey],
              ...value,
            };
          } else {
            mergedSettings[typedKey] = value;
          }
        });
        const settingsPayload = { settings: mergedSettings };
        const response = await axios.patch("/account", settingsPayload);
        if (this.account) {
          this.account.accountSettings = mergedSettings; // Update local state with merged settings
        }
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error, "Failed to update account settings"),
        });
        return null;
      }
    },
    async fetchKioskSettings(
      options: { suppressErrorToast?: boolean } = {},
    ): Promise<KioskSettingsResponse | null> {
      try {
        const response = await axios.get("/account", {
          params: {
            kioskSettings: true,
          },
        });

        return response.data;
      } catch (error) {
        if ((error as { status?: number } | null)?.status === 404) {
          return null;
        }

        if (!options.suppressErrorToast) {
          useSnackBarStore().toastError({
            message: getErrorMessage(error, "Failed to load kiosk settings"),
          });
        }
        return null;
      }
    },
    async updateKioskSettings(
      payload: KioskSettingsPatchPayload,
    ): Promise<KioskSettingsResponse | null> {
      try {
        const response = await axios.patch("/account", payload);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error, "Failed to update kiosk settings"),
        });
        return null;
      }
    },
    async generateKioskTokenHash(): Promise<GenerateKioskTokenHashResponse | null> {
      try {
        const response = await axios.post("/account/settings/kiosk/tokenHash");
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error, "Failed to generate kiosk token hash"),
        });
        return null;
      }
    },
  },
});
